import React, { useEffect, useMemo, useRef, useState } from 'react';

const RATIOS = [['original', 'Original'], [1, 'Quadrado'], [4 / 5, 'Retrato'], [16 / 9, 'Paisagem']];

export default function ImageCropper({ file, aspect = 'original', fixedAspect = false, outputWidth = 1400, title = 'Ajustar imagem', onCancel, onConfirm }) {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  const viewport = useRef(null);
  const image = useRef(null);
  const drag = useRef(null);
  const [natural, setNatural] = useState({ width: 1, height: 1 });
  const [ratio, setRatio] = useState(aspect === 'original' ? 1 : Number(aspect));
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [, setViewportRevision] = useState(0);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  useEffect(() => {
    if (!viewport.current || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setViewportRevision(value => value + 1));
    observer.observe(viewport.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (aspect === 'original') setRatio(natural.width / natural.height);
    else setRatio(Number(aspect));
    setOffset({ x: 0, y: 0 });
    setZoom(1);
  }, [aspect, natural]);

  const geometry = () => {
    const box = viewport.current?.getBoundingClientRect();
    if (!box) return null;
    const base = Math.max(box.width / natural.width, box.height / natural.height);
    const scale = base * zoom;
    const width = natural.width * scale;
    const height = natural.height * scale;
    const maxX = Math.max(0, (width - box.width) / 2);
    const maxY = Math.max(0, (height - box.height) / 2);
    return { box, scale, width, height, maxX, maxY };
  };
  const clamp = value => {
    const g = geometry();
    if (!g) return value;
    return { x: Math.max(-g.maxX, Math.min(g.maxX, value.x)), y: Math.max(-g.maxY, Math.min(g.maxY, value.y)) };
  };
  const pointerDown = event => {
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { x: event.clientX, y: event.clientY, offset };
  };
  const pointerMove = event => {
    if (!drag.current) return;
    setOffset(clamp({ x: drag.current.offset.x + event.clientX - drag.current.x, y: drag.current.offset.y + event.clientY - drag.current.y }));
  };
  const chooseRatio = value => {
    setRatio(value === 'original' ? natural.width / natural.height : Number(value));
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };
  const confirm = async () => {
    const g = geometry();
    if (!g || !image.current) return;
    setBusy(true);
    try {
      const targetWidth = Math.min(outputWidth, Math.max(600, natural.width));
      const targetHeight = Math.round(targetWidth / ratio);
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const left = (g.box.width - g.width) / 2 + offset.x;
      const top = (g.box.height - g.height) / 2 + offset.y;
      const sx = Math.max(0, -left / g.scale);
      const sy = Math.max(0, -top / g.scale);
      const sw = Math.min(natural.width - sx, g.box.width / g.scale);
      const sh = Math.min(natural.height - sy, g.box.height / g.scale);
      canvas.getContext('2d').drawImage(image.current, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);
      const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      const blob = await new Promise(resolve => canvas.toBlob(resolve, type, .92));
      if (!blob) throw new Error('Não foi possível preparar o recorte.');
      const extension = type === 'image/png' ? 'png' : 'jpg';
      onConfirm(new File([blob], `imagem-ajustada.${extension}`, { type }));
    } finally {
      setBusy(false);
    }
  };
  const g = geometry();
  return <div className="image-crop-backdrop" onClick={event => event.target === event.currentTarget && onCancel()}><section className="image-crop-modal">
    <header><button onClick={onCancel}><i className="ti ti-x" /></button><div><small>ATELIÊ DE ENQUADRAMENTO</small><b>{title}</b></div><img src="/blop-mini.png" alt="" /></header>
    <div ref={viewport} className="image-crop-viewport" style={{ aspectRatio: ratio }} onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={() => { drag.current = null; }} onPointerCancel={() => { drag.current = null; }}>
      <img ref={image} src={url} alt="Imagem para ajustar" draggable="false" onLoad={event => setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })} style={g ? { width: g.width, height: g.height, transform: `translate(${offset.x}px,${offset.y}px)` } : undefined} />
      <span className="image-crop-grid" />
    </div>
    <p>Arraste para escolher o melhor ângulo.</p>
    {!fixedAspect && <div className="image-crop-ratios">{RATIOS.map(([value, label]) => <button key={value} className={(value === 'original' ? Math.abs(ratio - natural.width / natural.height) < .01 : Math.abs(ratio - value) < .01) ? 'active' : ''} onClick={() => chooseRatio(value)}>{label}</button>)}</div>}
    <label className="image-crop-zoom"><i className="ti ti-minus" /><input type="range" min="1" max="3" step=".01" value={zoom} onChange={event => { setZoom(Number(event.target.value)); setOffset(current => clamp(current)); }} /><i className="ti ti-plus" /></label>
    <footer><button onClick={onCancel}>Cancelar</button><button className="confirm" disabled={busy} onClick={confirm}><i className="ti ti-crop" />{busy ? 'Preparando…' : 'Usar este enquadramento'}</button></footer>
  </section></div>;
}
